import { Link } from 'react-router-dom';

/**
 * A crumb. A plain string is a label; an object with `to` navigates.
 *
 * The breadcrumb IS the back-navigation (reference §6), which is why detail pages carry no
 * redundant "Back" button — so the ancestor crumbs of a detail page have to be links.
 */
export type BreadcrumbSegment = string | { label: string; to: string };

/**
 * The breadcrumb strip that tops every index and detail page — reference §3 and §7.
 *
 * `Section › Subsection › Page`, uniform weight throughout — the current page is
 * distinguished by colour alone, never by bold. The last crumb is where the user already
 * is, so it never links even if a `to` is supplied.
 */
export function Breadcrumb({ segments }: { segments: BreadcrumbSegment[] }) {
  return (
    <nav aria-label="Breadcrumb" className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
      <ol className="flex flex-wrap items-center gap-1.5 text-sm">
        {segments.map((segment, index) => {
          const label = typeof segment === 'string' ? segment : segment.label;
          const to = typeof segment === 'string' ? null : segment.to;
          const isCurrent = index === segments.length - 1;

          return (
            <li key={label} className="flex items-center gap-1.5">
              {index > 0 ? (
                <span className="text-slate-300" aria-hidden="true">
                  ›
                </span>
              ) : null}

              {to && !isCurrent ? (
                <Link
                  to={to}
                  className="rounded text-slate-600 transition hover:text-slate-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                >
                  {label}
                </Link>
              ) : (
                <span
                  className={isCurrent ? 'text-slate-800' : 'text-slate-600'}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
